"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { onBlurFormatSsn } from "#app/lib/schemas/field-formatters";
import { Alert, AlertContent, AlertTitle } from "#ui/alert";
import { Button } from "#ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "#ui/card";
import { DatePicker } from "#ui/date-picker";
import { Field } from "#ui/field";
import { FieldLabel } from "#ui/field-label";
import { FieldMessage } from "#ui/field-message";
import { Icon } from "#ui/icon";
import { Link } from "#ui/link";
import { PasswordInput } from "#ui/password-input";
import { PhoneInput } from "#ui/phone-input";
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "#ui/popover";
import { TextInput } from "#ui/text-input";
import { H2 } from "#ui/typography";

import {
  ERROR_MESSAGES,
  getDateOfBirthCalendarProps,
  RegisterStep0FormSchema,
  type RegisterStep0FormValues,
} from "./register-client-utils";

export function RegisterClientStep0({
  asyncError,
  isPending,
  onSubmitSuccess,
  ...props
}: React.ComponentPropsWithoutRef<"div"> & {
  asyncError: string | null;
  isPending: boolean;
  onSubmitSuccess: (formData: RegisterStep0FormValues) => void;
}) {
  const {
    control,
    formState: { errors, isSubmitted, isValid },
    handleSubmit,
    register,
  } = useForm<RegisterStep0FormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      ssn: "",
      email: "",
      phone: "",
      zip: "",
    },
    resolver: zodResolver(RegisterStep0FormSchema),
    mode: "onTouched",
  });

  // For showing a form-level error
  const isFormError = isSubmitted && !isValid;

  return (
    <Card {...props}>
      <CardHeader>
        <H2 size="h6">Personal information</H2>
      </CardHeader>
      <CardContent>
        <form
          id="client-registration-pii-form"
          onSubmit={handleSubmit((formValues) => onSubmitSuccess(formValues))}
          className="space-y-30px"
        >
          <div className="space-y-10px">
            <Alert intent="info">
              <AlertContent>
                <AlertTitle>
                  Enter your personal information that matches what we have on
                  file. If you need help,{" "}
                  <Link href="/tech-support">contact us</Link>.
                </AlertTitle>
              </AlertContent>
            </Alert>

            {isFormError || asyncError ? (
              <Alert intent="error" role="alert">
                <AlertContent>
                  <AlertTitle>
                    {asyncError ? asyncError : ERROR_MESSAGES.correctErrors}
                  </AlertTitle>
                </AlertContent>
              </Alert>
            ) : null}
          </div>

          <div className="grid gap-x-30px gap-y-20px md:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="firstName">First Name</FieldLabel>
              <TextInput
                {...register("firstName")}
                id="firstName"
                autoComplete="given-name"
                aria-invalid={errors.firstName?.message ? "true" : undefined}
                aria-describedby={
                  errors.firstName?.message ? "firstName-error" : undefined
                }
              />
              <FieldMessage id="firstName-error" intent="error">
                {errors.firstName?.message}
              </FieldMessage>
            </Field>

            <Field>
              <FieldLabel htmlFor="lastName">Last Name</FieldLabel>
              <TextInput
                {...register("lastName")}
                id="lastName"
                autoComplete="family-name"
                aria-invalid={errors.lastName?.message ? "true" : undefined}
                aria-describedby={
                  errors.lastName?.message ? "lastName-error" : undefined
                }
              />
              <FieldMessage id="lastName-error" intent="error">
                {errors.lastName?.message}
              </FieldMessage>
            </Field>

            <Field>
              <FieldLabel htmlFor="dateOfBirth">Date of Birth</FieldLabel>
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    autoComplete="off"
                    calendarProps={getDateOfBirthCalendarProps()}
                    id={field.name}
                    aria-invalid={
                      errors.dateOfBirth?.message ? "true" : undefined
                    }
                    aria-describedby={
                      errors.dateOfBirth?.message
                        ? "dateOfBirth-error"
                        : undefined
                    }
                  />
                )}
              />
              <FieldMessage id="dateOfBirth-error" intent="error">
                {errors.dateOfBirth?.message}
              </FieldMessage>
            </Field>

            <Field>
              <FieldLabel htmlFor="ssn">Social Security Number</FieldLabel>
              <Controller
                name="ssn"
                control={control}
                render={({ field }) => (
                  <PasswordInput
                    {...field}
                    id={field.name}
                    onBlur={(event) =>
                      onBlurFormatSsn({
                        event,
                        onBlur: field.onBlur,
                        onChange: field.onChange,
                      })
                    }
                    placeholder="xxx-xx-xxxx"
                    autoComplete="off"
                    aria-invalid={errors.ssn?.message ? "true" : undefined}
                    aria-describedby={
                      errors.ssn?.message ? "ssn-error" : undefined
                    }
                  />
                )}
              />
              <FieldMessage id="ssn-error" intent="error">
                {errors.ssn?.message}
              </FieldMessage>
            </Field>

            <Field>
              <div className="flex items-center gap-4px leading-none">
                <FieldLabel
                  htmlFor="email"
                  className="flex items-center leading-none"
                >
                  Email Address
                </FieldLabel>
                <Popover>
                  <PopoverTrigger id="email-popover">
                    <Icon name="ms-info" palette="accent" size="sm" />
                    <span className="sr-only">More info</span>
                  </PopoverTrigger>
                  <PopoverPortal>
                    <PopoverContent palette="black" size="sm" side="top">
                      <p>
                        This should match the email address we have on file.
                      </p>
                      <PopoverArrow palette="black" />
                    </PopoverContent>
                  </PopoverPortal>
                </Popover>
              </div>
              <TextInput
                {...register("email")}
                type="email"
                id="email"
                autoComplete="email"
                aria-invalid={errors.email?.message ? "true" : undefined}
                aria-describedby={
                  errors.email?.message
                    ? ["email-error", "email-popover"].join(" ")
                    : "email-popover"
                }
              />
              <FieldMessage id="email-error" intent="error">
                {errors.email?.message}
              </FieldMessage>
            </Field>

            <Field>
              <div className="flex items-center gap-4px leading-none">
                <FieldLabel htmlFor="phone">Phone Number</FieldLabel>
                <Popover>
                  <PopoverTrigger id="phone-popover">
                    <Icon name="ms-info" palette="accent" size="sm" />
                    <span className="sr-only">More info</span>
                  </PopoverTrigger>
                  <PopoverPortal>
                    <PopoverContent palette="black" size="sm" side="top">
                      <p>This should match the phone number we have on file.</p>
                      <PopoverArrow palette="black" />
                    </PopoverContent>
                  </PopoverPortal>
                </Popover>
              </div>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    id={field.name}
                    autoComplete="tel"
                    aria-invalid={errors.phone?.message ? "true" : undefined}
                    aria-describedby={
                      errors.phone?.message
                        ? ["phone-error", "phone-popover"].join(" ")
                        : "phone-popover"
                    }
                  />
                )}
              />
              <FieldMessage id="phone-error" intent="error">
                {errors.phone?.message}
              </FieldMessage>
            </Field>

            <Field>
              <div className="flex items-center gap-4px leading-none">
                <FieldLabel htmlFor="zip">ZIP Code</FieldLabel>
                <Popover>
                  <PopoverTrigger id="zip-popover">
                    <Icon name="ms-info" palette="accent" size="sm" />
                    <span className="sr-only">More info</span>
                  </PopoverTrigger>
                  <PopoverPortal>
                    <PopoverContent palette="black" size="sm" side="top">
                      <p>
                        This should match the ZIP code of the legal address we
                        have on file. It could differ from your mailing address
                        ZIP code.
                      </p>
                      <PopoverArrow palette="black" />
                    </PopoverContent>
                  </PopoverPortal>
                </Popover>
              </div>
              <TextInput
                {...register("zip")}
                id="zip"
                autoComplete="postal-code"
                aria-invalid={errors.zip?.message ? "true" : undefined}
                aria-describedby={
                  errors.zip?.message
                    ? ["zip-error", "zip-popover"].join(" ")
                    : "zip-popover"
                }
              />
              <FieldMessage id="zip-error" intent="error">
                {errors.zip?.message}
              </FieldMessage>
            </Field>
          </div>
        </form>
      </CardContent>
      <CardFooter>
        <Button
          type="submit"
          form="client-registration-pii-form"
          pending={isPending}
          disabled={isPending}
        >
          Next
        </Button>
      </CardFooter>
    </Card>
  );
}
